import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RepeatIcon from "@mui/icons-material/Repeat";
import CalendarIcon from "@mui/icons-material/CalendarToday";

const ReminderCardView = ({
  reminders,
  loading,
  onEdit,
  onDelete,
  onReschedule,
  onStatusChange,
  getCategoryLabel,
  getCategoryColor,
  getStatusLabel,
  getStatusColor,
  statusOptions,
  isToday,
  isPastDue,
  formatDate,
  formatDateTime,
  getStatusVisuals,
  getStatusSelectSx,
}) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!reminders || reminders.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
          p: 3,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No reminders found
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        pb: 4,
      }}
    >
      {reminders.map((reminder) => {
        const today = isToday(reminder);
        const pastDue = isPastDue(reminder);
        const statusKey = reminder.status || "pending";
        const statusVis = getStatusVisuals ? getStatusVisuals(statusKey) : null;
        let bgColor = statusVis?.rowBg || "background.paper";
        if (today) bgColor = "rgba(255, 193, 7, 0.18)";
        if (pastDue) bgColor = "rgba(244, 67, 54, 0.14)";
        const borderColor = statusVis?.border || "rgba(255, 255, 255, 0.1)";

        return (
          <Card
            key={reminder.id}
            sx={{
              width: "100%",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
              borderRadius: "12px",
              overflow: "visible",
              bgcolor: bgColor,
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderLeft: `4px solid ${borderColor}`,
              ...(today && { boxShadow: "0 0 0 2px rgba(255, 193, 7, 0.45)" }),
              ...(pastDue && !today && { boxShadow: "0 0 0 2px rgba(244, 67, 54, 0.35)" }),
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              {/* Header: Title and Status */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 1.5,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "18px",
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    {reminder.title}
                  </Typography>
                  {today && (
                    <Chip
                      label="Today"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(255, 193, 7, 0.3)",
                        color: "#fff",
                        fontSize: "10px",
                        height: "20px",
                        mt: 0.5,
                      }}
                    />
                  )}
                  {pastDue && (
                    <Chip
                      label="Past Due"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(244, 67, 54, 0.3)",
                        color: "#fff",
                        fontSize: "10px",
                        height: "20px",
                        mt: 0.5,
                      }}
                    />
                  )}
                </Box>
                <Chip
                  label={getCategoryLabel(reminder.category)}
                  size="small"
                  color={getCategoryColor(reminder.category)}
                  sx={{ fontSize: "11px", height: "24px" }}
                />
              </Box>

              {formatDateTime && reminder.created_at && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Created {formatDateTime(reminder.created_at)}
                </Typography>
              )}

              {/* Date and Time */}
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>
                  Reminder details
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarIcon sx={{ fontSize: "18px", color: "text.secondary" }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatDate(reminder.reminder_date)}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 4, fontSize: "12px" }}
                >
                  {reminder.reminder_time || "09:00"} (UK)
                </Typography>
              </Box>

              {/* Description */}
              {reminder.description && (
                <Box
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "8px",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "13px", color: "text.secondary" }}
                  >
                    {reminder.description}
                  </Typography>
                </Box>
              )}

              {/* Clients */}
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>
                  Recipients:
                </Typography>
                {reminder.client_emails && reminder.client_emails.length > 0 ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: "13px" }}>
                      {reminder.client_emails.length} client(s)
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "12px" }}
                    >
                      {reminder.client_emails.slice(0, 3).join(", ")}
                      {reminder.client_emails.length > 3
                        ? ` +${reminder.client_emails.length - 3} more`
                        : ""}
                    </Typography>
                  </Box>
                ) : reminder.custom_email ? (
                  <Typography variant="body2" sx={{ fontSize: "13px", mt: 0.5 }}>
                    {reminder.custom_email}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ fontSize: "13px", mt: 0.5 }}>
                    Admin only
                  </Typography>
                )}
              </Box>

              {/* Notes */}
              {reminder.notes && (
                <Box
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "8px",
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>
                    Notes:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: "13px", mt: 0.5 }}>
                    {reminder.notes}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />

              {/* Status and Actions */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                {/* Status Select */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px", mb: 0.5, display: "block" }}>
                    Status:
                  </Typography>
                  <Select
                    value={reminder.status || "pending"}
                    onChange={async (e) => {
                      if (onStatusChange) {
                        await onStatusChange(reminder.id, e.target.value);
                      }
                    }}
                    size="small"
                    fullWidth
                    sx={{
                      height: "40px",
                      fontSize: "13px",
                      fontWeight: 600,
                      ...(getStatusSelectSx
                        ? getStatusSelectSx(reminder.status || "pending")
                        : {}),
                    }}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    justifyContent: "space-between",
                  }}
                >
                  <Tooltip title="Edit">
                    <IconButton
                      size="medium"
                      onClick={() => onEdit && onEdit(reminder)}
                      sx={{
                        background: "rgba(42, 245, 152, 0.2)",
                        color: "#2af598",
                        flex: 1,
                        height: "44px",
                        borderRadius: "8px",
                        "&:hover": {
                          background: "rgba(42, 245, 152, 0.3)",
                        },
                      }}
                    >
                      <EditIcon sx={{ fontSize: "20px" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reschedule">
                    <IconButton
                      size="medium"
                      onClick={() => onReschedule && onReschedule(reminder)}
                      sx={{
                        background: "rgba(58, 222, 204, 0.2)",
                        color: "#3adecc",
                        flex: 1,
                        height: "44px",
                        borderRadius: "8px",
                        "&:hover": {
                          background: "rgba(58, 222, 204, 0.3)",
                        },
                      }}
                    >
                      <RepeatIcon sx={{ fontSize: "20px" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="medium"
                      onClick={() => onDelete && onDelete(reminder.id)}
                      sx={{
                        background: "rgba(244, 67, 54, 0.2)",
                        color: "#f44336",
                        flex: 1,
                        height: "44px",
                        borderRadius: "8px",
                        "&:hover": {
                          background: "rgba(244, 67, 54, 0.3)",
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: "20px" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default ReminderCardView;
